// Do NOT intercept 401 here - let the SDK HttpClient handle refresh first.
// A global 401 redirect prevented the SDK from attempting token refresh, causing
// redirect loops when the access token expired (CRM and other routes).

import { createApp } from "vue";
import { createPinia } from "pinia";
import { VueQueryPlugin, QueryClient } from "@tanstack/vue-query";
import { vuetify } from "./plugins/vuetify";
import router from "./router";
import App from "./App.vue";
import "./styles/main.scss";
import { usePushNotifications } from "./composables/usePushNotifications";
import * as SentryCapacitor from "@sentry/capacitor";
import * as SentryVue from "@sentry/vue";
import { Capacitor } from "@capacitor/core";

// Get Sentry DSN from environment
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const sentryEnvironment = import.meta.env.MODE || 'production';

// Create Vue app first (needed for Sentry Vue integration)
const app = createApp(App);

// Initialize Sentry for both web and native platforms
if (sentryDsn) {
  if (Capacitor.isNativePlatform()) {
    // Native (iOS/Android) - use Capacitor SDK
    SentryCapacitor.init({
      dsn: sentryDsn,
      environment: sentryEnvironment,
    });
  } else {
    // Web - use Vue SDK with full integration
    SentryVue.init({
      app,
      dsn: sentryDsn,
      environment: sentryEnvironment,
      integrations: [
        SentryVue.browserTracingIntegration({ router }),
        SentryVue.replayIntegration(),
        SentryVue.browserProfilingIntegration(),
      ],
      // Performance monitoring (required for profiling)
      tracesSampleRate: sentryEnvironment === 'production' ? 0.1 : 1.0,
      // Browser profiling sample rate (relative to tracesSampleRate)
      profilesSampleRate: 1.0,
      // Session Replay
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      // Send default PII (IP address, etc.)
      sendDefaultPii: true,
    });
  }
} else {
  console.warn('[Sentry] VITE_SENTRY_DSN not configured, skipping Sentry initialization');
}

// Create Pinia instance
const pinia = createPinia();

// Configure TanStack Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2, // 2 minutes - aggressive staleness for social media
      retry: 3,
      refetchOnWindowFocus: true, // Always check for updates when user returns
      refetchOnMount: true, // Check for updates when component mounts
      refetchOnReconnect: true, // Refetch when network reconnects
      // Cache backing via Dexie makes frequent refetches fast
    },
  },
});

// Configure Vue to handle warnings and errors
app.config.warnHandler = (msg, instance, trace) => {
  // Suppress known Vuetify lifecycle warnings that are non-critical
  const suppressedWarnings = [
    'onScopeDispose() is called when there is no active effect scope',
    'Missing ref owner context',
  ];

  if (suppressedWarnings.some(warning => msg.includes(warning))) {
    // These are known Vuetify 3 internal warnings that don't affect functionality
    return;
  }

  // Log other warnings normally
  console.warn(`[Vue warn]: ${msg}${trace}`);
};

app.config.errorHandler = (err, instance, info) => {
  // Log ALL errors with full context for debugging navigation issues
  const componentName = instance?.$options?.name || instance?.$.type?.name || 'Unknown';
  const routePath = router.currentRoute?.value?.fullPath || 'unknown';

  console.error(`[Vue error in ${componentName}]: ${info}`, {
    error: err,
    message: err instanceof Error ? err.message : String(err),
    stack: err instanceof Error ? err.stack : undefined,
    route: routePath,
    componentName,
    info,
  });

  // Send error to Sentry with context
  if (sentryDsn && err instanceof Error) {
    SentryVue.withScope((scope) => {
      scope.setTag('component', componentName);
      scope.setTag('route', routePath);
      scope.setContext('vue', { info, componentName });
      SentryVue.captureException(err);
    });
  }

  // Track consecutive errors that might indicate a broken state
  const errorCountKey = 'vue_error_count';
  const lastErrorTimeKey = 'vue_last_error_time';
  const currentCount = parseInt(sessionStorage.getItem(errorCountKey) || '0', 10);
  const lastErrorTime = parseInt(sessionStorage.getItem(lastErrorTimeKey) || '0', 10);
  const now = Date.now();

  // Reset counter if more than 30 seconds since last error
  if (now - lastErrorTime > 30_000) {
    sessionStorage.setItem(errorCountKey, '1');
  } else {
    sessionStorage.setItem(errorCountKey, String(currentCount + 1));
  }
  sessionStorage.setItem(lastErrorTimeKey, String(now));

  // If we get 5+ errors in quick succession, something is very wrong
  if (currentCount + 1 >= 5 && now - lastErrorTime < 30_000) {
    console.error('[Vue] Multiple errors detected in quick succession - app may be in broken state. Consider refreshing.');
  }
};

// Register plugins
app.use(pinia);
app.use(vuetify);
app.use(router);
app.use(VueQueryPlugin, { queryClient });

// Google Analytics (GA4) — only loads when VITE_GA_ID is set
const gaId = import.meta.env.VITE_GA_ID;
if (gaId) {
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
  document.head.appendChild(script);

  (window as any).dataLayer = (window as any).dataLayer || [];
  function gtag(...args: any[]) { (window as any).dataLayer.push(args); }
  gtag('js', new Date());
  gtag('config', gaId);
}

// Mount app
app.mount("#app");

// Initialize push notifications (only on native platforms)
const pushNotifications = usePushNotifications();
if (pushNotifications.isSupported.value) {
  pushNotifications.initialize().catch((error) => {
    console.error('[Main] Failed to initialize push notifications:', error);
  });
}

// Global error handlers for debugging navigation issues
if (typeof window !== 'undefined') {
  // Catch unhandled Promise rejections that might break navigation
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;
    const message = error instanceof Error ? error.message : String(error);

    console.error('[Unhandled Promise Rejection]', {
      message,
      error,
      stack: error instanceof Error ? error.stack : undefined,
      route: router.currentRoute?.value?.fullPath || 'unknown',
    });

    // Check if this looks like a chunk load failure
    const isChunkError =
      message.includes('Loading chunk') ||
      message.includes('ChunkLoadError') ||
      message.includes('dynamically imported module') ||
      message.includes('Failed to fetch') ||
      message.includes('Load failed');

    if (isChunkError) {
      console.warn('[Navigation] Chunk load failure detected via unhandled rejection. This may cause navigation issues.');
    }
  });

  // Catch global errors that might slip through
  window.addEventListener('error', (event) => {
    // Only log if it's a script error (not resource loading errors handled elsewhere)
    if (event.error) {
      console.error('[Global Error]', {
        message: event.message,
        error: event.error,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        route: router.currentRoute?.value?.fullPath || 'unknown',
      });
    }
  });
}

// Force rebuild 20260113.001
